/**
 * /admin/photos · founder photo-swap surface.
 *
 * Lists every Cloudinary brand asset, grouped by pillar, with a
 * one-click "Replace" button per card. Uploaded file overwrites the
 * existing public_id on Cloudinary so the live frontend picks up the
 * new photo within the CDN's edge cache window — no code redeploy.
 *
 * Founder ruling: "every photo is an editorial decision, not an
 * engineering one — give me a button to swap them in 60 seconds."
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { resizeImageForUpload } from "@/lib/imageResize";

const API = process.env.REACT_APP_BACKEND_URL || "";

const T = {
  bg:    "#F4EFE6",
  card:  "#FCFAF4",
  ink:   "#211C15",
  ink2:  "#5C5447",
  ink3:  "#8A8071",
  line:  "#E5DCCB",
  line2: "#D8CDB7",
  brass: "#8B6914",
  good:  "#2f8a52",
};

const PILLAR_LABELS = {
  airport:   "/airport · service tiles",
  mobility:  "/mobility · transport tiles",
  brand:     "Brand · logos",
  partners:  "Partners",
  home:      "/ · home page",
  about:     "/about",
  solutions: "/solutions · editorial",
};

export default function AdminPhotosPage() {
  const [items, setItems] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/admin/photos`);
        const j = await r.json();
        setItems(j.items || []);
        setGrouped(j.grouped || {});
      } catch (e) {
        console.error("Failed to load admin photos", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div
      data-testid="admin-photos-root"
      data-table-surface="admin-photos"
      style={{
        background: T.bg, minHeight: "100vh", color: T.ink,
        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
        "--t-bg": T.bg, "--t-ink": T.ink, "--t-ink2": T.ink2,
        "--t-ink3": T.ink3, "--t-line": T.line, "--t-brass": T.brass,
      }}
    >
      <header style={{
        borderBottom: `1px solid ${T.line}`,
        background: T.bg,
        position: "sticky", top: 0, zIndex: 20,
      }}>
        <div style={{
          maxWidth: 1180, margin: "0 auto",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 22px",
        }}>
          <Link to="/" data-testid="admin-photos-home" style={{ textDecoration: "none", color: T.ink, display: "inline-flex", alignItems: "baseline", gap: 10 }}>
            <span className="t-serif" style={{ fontSize: 22, fontWeight: 500 }}>/admin/photos</span>
            <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: T.ink3 }}>
              by Concierge®
            </span>
          </Link>
          <span style={{ fontSize: 12, color: T.ink3, fontFamily: "ui-monospace, monospace", letterSpacing: "0.10em", textTransform: "uppercase" }}>
            {items.length} brand assets · Cloudinary
          </span>
        </div>
      </header>

      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "40px 28px 80px" }}>
        <div style={{ marginBottom: 12 }}>
          <span style={{
            fontFamily: "ui-monospace, monospace", fontSize: 11,
            letterSpacing: "0.22em", color: T.brass, fontWeight: 600,
            textTransform: "uppercase",
          }}>Editorial control</span>
        </div>
        <h1 className="t-serif" style={{ fontSize: "clamp(36px, 4vw, 48px)", fontWeight: 400, lineHeight: 1.05, margin: 0 }}>
          Swap any brand photo in 60 seconds.
        </h1>
        <p style={{ color: T.ink2, fontSize: 15, marginTop: 12, maxWidth: 720, lineHeight: 1.5 }}>
          Every founder-curated photo across the platform lives in Cloudinary
          under a stable URL. Click <em>Replace</em>, pick a new file, and the
          live site picks it up automatically — no code change, no redeploy.
        </p>

        {loading ? (
          <p style={{ color: T.ink3, marginTop: 40, fontStyle: "italic" }}>Loading brand assets…</p>
        ) : (
          Object.keys(grouped).map((pillar) => (
            <PillarBlock
              key={pillar}
              pillar={pillar}
              label={PILLAR_LABELS[pillar] || pillar}
              items={grouped[pillar] || []}
            />
          ))
        )}
      </main>
    </div>
  );
}


function PillarBlock({ pillar, label, items }) {
  return (
    <section style={{ marginTop: 48 }} data-testid={`admin-photos-block-${pillar}`}>
      <h2 className="t-serif" style={{
        fontSize: 22, fontWeight: 500, margin: "0 0 14px",
        paddingBottom: 10, borderBottom: `1px solid ${T.line}`,
      }}>{label}</h2>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
        gap: 14,
      }}>
        {items.map((it) => (
          <PhotoCard key={`${it.folder}/${it.pid}`} item={it} />
        ))}
      </div>
    </section>
  );
}


function PhotoCard({ item }) {
  const fileRef = useRef(null);
  const [busted, setBusted] = useState("");           // cache-buster query string after successful upload
  const [uploading, setUploading] = useState(false);
  const [status,    setStatus]    = useState(null);   // {ok,msg}

  async function onPick(e) {
    const raw = e.target.files?.[0];
    if (!raw) return;
    setUploading(true);
    setStatus(null);
    try {
      // v3.48 · client-side resize — see /lib/imageResize.js
      const file = await resizeImageForUpload(raw);
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/api/admin/photos/${item.folder}/${item.pid}`, {
        method: "POST",
        body: fd,
      });
      const j = await r.json();
      if (r.ok && j.ok) {
        setBusted(`?_t=${j.version || Date.now()}`);
        setStatus({ ok: true, msg: `Swapped · ${(j.size_bytes / 1024).toFixed(0)} KB` });
      } else {
        setStatus({ ok: false, msg: j.detail || "Upload failed" });
      }
    } catch (e) {
      setStatus({ ok: false, msg: "Network error" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  return (
    <div data-testid={`admin-photo-card-${item.pid}`} style={{
      background: T.card,
      border: `1px solid ${T.line}`,
      borderRadius: 14,
      padding: 12,
      display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{
        width: "100%", aspectRatio: "3/2",
        backgroundImage: `url(${item.thumb_url}${busted})`,
        backgroundSize: "cover", backgroundPosition: "center",
        borderRadius: 10,
        border: `1px solid ${T.line}`,
      }} />
      <div>
        <div style={{ fontSize: 13, color: T.ink, fontWeight: 500 }}>
          {item.label}
        </div>
        <div style={{
          fontFamily: "ui-monospace, monospace", fontSize: 10,
          color: T.ink3, letterSpacing: "0.06em", marginTop: 2,
        }}>
          {item.folder}/{item.pid}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "auto" }}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={onPick}
          data-testid={`admin-photo-input-${item.pid}`}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`admin-photo-replace-${item.pid}`}
          style={{
            background: T.ink, color: "#FCFAF4",
            border: "none", borderRadius: 999,
            padding: "6px 14px",
            fontFamily: "inherit", fontSize: 12, fontWeight: 500,
            cursor: uploading ? "wait" : "pointer",
            opacity: uploading ? 0.6 : 1,
          }}>{uploading ? "Uploading…" : "Replace"}</button>
        <a href={`${item.url}${busted}`} target="_blank" rel="noreferrer"
          style={{
            fontSize: 12, color: T.ink3, textDecoration: "none",
            border: `1px solid ${T.line2}`, borderRadius: 999,
            padding: "5px 10px",
          }}>Open ↗</a>
        {status && (
          <span style={{
            marginLeft: "auto", fontSize: 11,
            color: status.ok ? T.good : "#a04545",
            fontFamily: "ui-monospace, monospace",
          }} data-testid={`admin-photo-status-${item.pid}`}>{status.msg}</span>
        )}
      </div>
    </div>
  );
}
